import LoadingIcon from "../../../common/LoadingIcon";

import {
  COLORS,
  FlashPCBTable,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from "../../../../constants/Styles";
import {
  Board,
  CheckResult,
  CheckResultStatus,
  PhysicalDetailsState,
} from "../../../../reducers/uploadedBoard";
import RuleCheckResult from "./RuleCheck";
import { useCallback, useEffect, useState } from "react";
import { useInterval } from "@restart/hooks";
import { useBoardFromContext } from "../../../../utils/hooks/commonly-stored/uploaded-board/board";
import { useAppSelector } from "../../../../utils/hooks/store";
import {
  DRC_EXPLANATION_FLAG,
  TRUE_FLAG,
} from "../../../../reducers/models/flags";
import {
  trackSawDRCProcessing,
  trackSawDRCResults,
} from "../../../../utils/observability/boardTracking";
import {
  Container,
  Result,
  ResultMessageContainer,
  ResultsContainer,
  StatusMessage,
  TableContainer,
} from "./RuleChecksStyles";
import { ErrorIcon, PassIcon } from "./ResultLine";
import Checkbox from "../../../common/Checkbox";

const PROCESSING_MESSAGES = [
  "Running design rule checks (this can take a couple minutes).",
  "Don't worry you can leave this page and we'll continue processing your board. Find this board and all your others on your account page.",
  "If this takes more than 5 minutes we'll send you an email letting you know when it's ready.",
];

const DRC_EXPLANATION_PROCESSING_MESSAGES = [
  "Running design rule checks (this can take a couple minutes).",
  "Our design rule checks guarantee you receive an accurate quote and delivery timeline.",
  "These checks ensure that we can manufacture your board efficiently, eliminating frustrating email chains with engineers and ensuring timely PCB assembly.",
  "We are preparing a preview of your final manufacturing files to ensure that the design you see is the same as the design you get.",
];

function Results({
  groupName,
  results,
}: {
  groupName: string;
  results: CheckResult[];
}) {
  if (results.length == 0) {
    return null;
  }
  return (
    <TableRow>
      <TableCell>{groupName}</TableCell>
      <TableCell>
        {results.some((result) => result.status == CheckResultStatus.ERROR) ? (
          <StatusMessage color={COLORS.error}>Error</StatusMessage>
        ) : (
          <StatusMessage color={COLORS.secondaryLight}>Warning</StatusMessage>
        )}
      </TableCell>
      <TableCell>
        <ResultsContainer>
          {results.map((result) => {
            return <RuleCheckResult key={result.result_id} result={result} />;
          })}
        </ResultsContainer>
      </TableCell>
    </TableRow>
  );
}

export default function RuleChecks() {
  const { flags } = useAppSelector((state) => state.flags);
  const showDRCExplanationMessages =
    flags !== null && flags[DRC_EXPLANATION_FLAG] === TRUE_FLAG;
  const processingMessages = showDRCExplanationMessages
    ? DRC_EXPLANATION_PROCESSING_MESSAGES
    : PROCESSING_MESSAGES;
  const board = useBoardFromContext().board as Board;
  const { file_check_results, panel_check_results } = board;
  const [processingMessageIndex, setProcessingMessageIndex] = useState(0);
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const errorsExist =
    file_check_results.some(
      (result) => result.status == CheckResultStatus.ERROR,
    ) ||
    panel_check_results.some(
      (result) => result.status == CheckResultStatus.ERROR,
    );
  const runningChecks =
    board.physical_details_state != PhysicalDetailsState.PROCESSED;

  useEffect(() => {
    if (runningChecks) {
      trackSawDRCProcessing(board);
    } else {
      trackSawDRCResults(board);
    }
  }, [board, runningChecks]);

  const cycleProcessingMessages = useCallback(() => {
    setProcessingMessageIndex(
      (processingMessageIndex + 1) % processingMessages.length,
    );
  }, [processingMessageIndex, processingMessages.length]);

  useInterval(
    cycleProcessingMessages,
    showDRCExplanationMessages ? 8000 : 15000,
  );

  if (file_check_results.length == 0 && panel_check_results.length == 0) {
    if (runningChecks) {
      return (
        <LoadingIcon
          text={processingMessages[processingMessageIndex]}
          styleProps={{ marginTop: "1.5rem" }}
        />
      );
    } else {
      if (board.physical_details === null) {
        return (
          <Result>
            <ErrorIcon />
            <ResultMessageContainer>
              We were unable to process your board! We have received a
              notification of this and will investigate. Please reach out to{" "}
              <a href="mailto:help@flashpcb.com">help@flashpcb.com</a> if you
              have any specific questions or concerns.
            </ResultMessageContainer>
          </Result>
        );
      } else {
        return (
          <Result>
            <PassIcon />
            <ResultMessageContainer>All checks pass!</ResultMessageContainer>
          </Result>
        );
      }
    }
  }

  return (
    <Container>
      {errorsExist && (
        <Checkbox
          label={"Show errors only"}
          checked={showErrorsOnly}
          onChange={() => setShowErrorsOnly(!showErrorsOnly)}
        />
      )}
      <TableContainer runningAdditionalChecks={runningChecks}>
        <FlashPCBTable>
          <thead>
            <TableHeaderRow>
              <TableHeaderCell sticky width="12rem">
                Checks
              </TableHeaderCell>
              <TableHeaderCell sticky width="8rem">
                Result
              </TableHeaderCell>
              <TableHeaderCell sticky>Details</TableHeaderCell>
            </TableHeaderRow>
          </thead>
          <tbody>
            <Results
              groupName={"File checks"}
              results={
                showErrorsOnly
                  ? file_check_results.filter(
                      (result) => result.status == CheckResultStatus.ERROR,
                    )
                  : file_check_results
              }
            />
            <Results
              groupName={"Panel checks"}
              results={
                showErrorsOnly
                  ? panel_check_results.filter(
                      (result) => result.status == CheckResultStatus.ERROR,
                    )
                  : panel_check_results
              }
            />
          </tbody>
        </FlashPCBTable>
      </TableContainer>
    </Container>
  );
}
